import { useCallback } from 'react';
import { Button, TextField } from '@finpay-development/shared-components';
import { Grid, Button as MUIButton, Radio, RadioGroup, FormControlLabel, Typography, Box } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { AttachMoney } from '@mui/icons-material';
import { formatNumberToUSD } from 'src/shared/calculators';
import { riskClassNameLookup } from 'src/shared/model/risk-class';
import type { PaymentProgramViewContext } from './use-payment-program-view';

interface HreApprovalViewProps {
  ctx: PaymentProgramViewContext;
}

function hreCopyPaymentLine(isWeekly: boolean, recurringPerPeriod: number): string {
  return isWeekly
    ? `Weekly Payment: ${formatNumberToUSD(recurringPerPeriod)}`
    : `Monthly Payment: ${formatNumberToUSD(recurringPerPeriod)}`;
}

function hreCopyTermLine(isWeekly: boolean, termWeeks: number, termMonths: number): string {
  return isWeekly
    ? `Term: ${termWeeks} ${termWeeks === 1 ? 'Week' : 'Weeks'}`
    : `Term: ${termMonths} ${termMonths === 1 ? 'Month' : 'Months'}`;
}

function buildApplicableRuleText(ctx: PaymentProgramViewContext): string {
  const { matchingRule, pfrAmt } = ctx;
  if (!matchingRule?.ruleId) return '';
  const parts: string[] = [];
  const minDown = matchingRule.minDownPmt ?? matchingRule.optimalDownPmt;
  const minType = matchingRule.minDownPmtType ?? matchingRule.optimalDownPmtType;
  if (minDown != null && minType) {
    const downDollar =
      minType === '%' ? ((Number(minDown) || 0) * pfrAmt) / 100 : Number(minDown) || 0;
    parts.push(`${minType === '%' ? `${minDown}%` : ''} Down (${formatNumberToUSD(downDollar)})`);
  }
  const maxTerm = matchingRule.optimalTerm;
  if (maxTerm) parts.push(`Max ${maxTerm} months`);
  return parts.join(', ');
}

function buildInstanceOfCareDetailsText(ctx: PaymentProgramViewContext): string {
  const {
    activeEncounter,
    activePatient,
    pfrAmt,
    adjustedPfrAmt,
    matchingRule,
    optDownPayment,
    recurringAmt,
    customDownPmt,
    customRecurringAmtFinal,
    calculatedTerm,
    calculatedTermDisplay,
    optimalTermNum,
    optimalTermNumDisplay,
    billingFrequency,
    payersMap,
    linkedEstimateAdjustedCharges,
    linkedEstimateEiv,
    linkedEstimateRateAsPayorInNetwork,
    linkedEstimateRateAsPayorPayorId,
  } = ctx;
  const ioc = activeEncounter;

  const patientName =
    activePatient?.contact
      ? [activePatient.contact.firstName, activePatient.contact.lastName].filter(Boolean).join(' ').trim() || 'Not Available'
      : 'Not Available';
  const facility = ioc?.facilityName ?? 'Not Available';
  const levelOfCare = ioc?.levelOfCare?.trim() ? ioc.levelOfCare : 'Not Available';

  const estimatedGrossCharges =
    linkedEstimateAdjustedCharges != null && linkedEstimateAdjustedCharges >= 0
      ? formatNumberToUSD(linkedEstimateAdjustedCharges)
      : 'Not Available';

  const payor =
    linkedEstimateRateAsPayorPayorId != null
      ? (payersMap?.get(linkedEstimateRateAsPayorPayorId)?.payorName ?? '').trim() || 'Not Available'
      : 'Not Available';
  const payorTypeFromEncounterRisk = ioc?.payorRisk?.payorRiskName?.trim() || '';
  const payorNorm = payor.replace(/\s+/g, ' ').trim().toLowerCase();
  const payorResolved = payorNorm !== 'not available';
  const isSelfPayPayor = payorNorm === 'self-pay' || payorNorm === 'self pay';
  const payorTypeFromEstimate =
    typeof linkedEstimateRateAsPayorInNetwork === 'boolean'
      ? linkedEstimateRateAsPayorInNetwork
        ? 'In Network'
        : 'Out of Network'
      : null;
  const appendPayorType =
    payorResolved &&
    !isSelfPayPayor &&
    (payorTypeFromEstimate != null || !!payorTypeFromEncounterRisk);
  const payorTypeSuffix = payorTypeFromEstimate ?? payorTypeFromEncounterRisk;

  const eiv =
    linkedEstimateEiv != null && linkedEstimateEiv >= 0
      ? formatNumberToUSD(linkedEstimateEiv)
      : 'Not Available';
  const pfrEstimate = formatNumberToUSD(adjustedPfrAmt ?? pfrAmt);
  const riskClassLabel =
    ioc?.riskClass?.riskClassName?.trim() ||
    (ioc?.riskClass?.riskClassId != null ? riskClassNameLookup(ioc.riskClass.riskClassId) : '') ||
    'Not Available';

  const downPayment = customDownPmt ?? optDownPayment ?? 0;
  const isWeeklySchedule = billingFrequency === 'weekly';
  const recurringPerPeriod = customRecurringAmtFinal ?? recurringAmt ?? 0;
  const termWeeks = calculatedTermDisplay || optimalTermNumDisplay || 0;
  const termMonths = calculatedTerm || optimalTermNum || 0;
  const applicableRule = buildApplicableRuleText(ctx);

  const lines: string[] = [
    'Based on the financial clearance rules the patient has failed financial clearance, resulting in a "HIGH RISK" status.  Please review the information below to determine if an exception should be made for this patient.',
    '',
    `Patient Name: ${patientName}`,
    `Facility: ${facility}`,
    `Level of Care: ${levelOfCare}`,
    '',
    `Estimated Adjusted Gross Charges: ${estimatedGrossCharges}`,
    appendPayorType && payorTypeSuffix ? `Payor: ${payor} — ${payorTypeSuffix}` : `Payor: ${payor}`,
    `EIV: ${eiv}`,
    `Patient Financial Responsibility Estimate: ${pfrEstimate}`,
    riskClassLabel !== 'Not Available' ? riskClassLabel : 'Risk Class: Not Available',
    '',
    'Requested High Risk Program: ',
    `Down Payment: ${formatNumberToUSD(downPayment)}`,
    hreCopyPaymentLine(isWeeklySchedule, recurringPerPeriod),
    hreCopyTermLine(isWeeklySchedule, termWeeks, termMonths),
    ...(applicableRule ? [`Applicable Risk Class Rule: ${applicableRule}`] : []),
    '',
    `Review VOB and Estimate: ${typeof window !== 'undefined' && window.location?.origin ? window.location.origin : ''}/patients/${ctx.patientId}`,
    `Review IOC: ${typeof window !== 'undefined' && window.location?.origin ? window.location.origin : ''}/specialist/dashboard/${ctx.patientId}?encounter=${ctx.encounterId}`,
    `Respond:  ${typeof window !== 'undefined' && window.location?.origin ? window.location.origin : ''}/hub/patient/${ctx.patientId}/ioc/${ctx.encounterId}/program/hre-approval`,
  ];

  const reviewVobIdx = lines.findIndex((l) => l.startsWith('Review VOB and Estimate:'));
  if (reviewVobIdx >= 0) lines.splice(reviewVobIdx, 0, '');
  const respondIdx = lines.findIndex((l) => l.startsWith('Respond:'));
  if (respondIdx >= 0) lines.splice(respondIdx, 0, '');

  return lines.join('\n');
}

/**
 * HRE (High Risk Exception) approval step: instructions, Copy Details button, status radios,
 * amendment fields when applicable, HRE Response textarea, Back and Save / Add Payment Details.
 */
export function HreApprovalView({ ctx }: HreApprovalViewProps) {
  const {
    hreApprovalStatus,
    setHreApprovalStatus,
    approvalResponse,
    setApprovalResponse,
    customDownPayment,
    customRecurringAmount,
    downPaymentError,
    recurringAmountError,
    handleBack,
    handleSelectCustomProgram,
    handleDownPaymentChange,
    handleRecurringAmountChange,
    handleDownPaymentBlur,
    handleRecurringAmountBlur,
  } = ctx;

  const isRejected =
    hreApprovalStatus === 'rejected-by-client' || hreApprovalStatus === 'rejected-by-guarantor';
  const isApproved =
    hreApprovalStatus === 'approved' || hreApprovalStatus === 'approved-with-amendment';
  const showSaveButton = !hreApprovalStatus || isRejected;
  const showAddPaymentDetailsButton = isApproved;

  const handleCopyDetails = useCallback(() => {
    const text = buildInstanceOfCareDetailsText(ctx);
    void navigator.clipboard?.writeText(text)?.catch(() => {});
  }, [ctx]);

  return (
    <Grid item xs={12}>
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="body1" component="span">
              <strong>Step 1:</strong> Email the facility with the details of this instance of care.
            </Typography>
            <MUIButton
              variant="outlined"
              size="small"
              onClick={handleCopyDetails}
              test-id="hre-copy-details"
            >
              Copy Details
            </MUIButton>
          </Box>
          <Typography variant="body1" component="div" sx={{ mb: 2 }}>
            <strong>Step 2:</strong> Select an option below and provide details received from the facility in the HRE Response box.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <RadioGroup
            value={hreApprovalStatus}
            onChange={(e) => setHreApprovalStatus(e.target.value)}
            name="hre-approval-status"
          >
            <FormControlLabel value="approved" control={<Radio />} label="Approved" />
            {/* <FormControlLabel value="approved-with-amendment" control={<Radio />} label="Approved with Amendment" /> */}
            <FormControlLabel value="rejected-by-client" control={<Radio />} label="Rejected" />
            {/* <FormControlLabel value="rejected-by-guarantor" control={<Radio />} label="Rejected by Guarantor" /> */}
          </RadioGroup>
        </Grid>
        {hreApprovalStatus === 'approved-with-amendment' && (
          <>
            <Grid item xs={12} md={6}>
              <Box sx={{ minWidth: 0, '& input': { textAlign: 'right' }, '& .MuiInputBase-root': { height: 56 } }}>
                <TextField
                  label="Down Payment"
                  name="downPayment"
                  type="text"
                  value={customDownPayment}
                  onChange={handleDownPaymentChange}
                  onBlur={handleDownPaymentBlur}
                  error={!!downPaymentError}
                  helperText={downPaymentError}
                  placeholder="0.00"
                  startAdornment={<AttachMoney />}
                  test-id="hre-down-payment"
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ minWidth: 0, '& input': { textAlign: 'right' }, '& .MuiInputBase-root': { height: 56 } }}>
                <TextField
                  label="Recurring Amount"
                  name="recurringAmount"
                  type="text"
                  value={customRecurringAmount}
                  onChange={handleRecurringAmountChange}
                  onBlur={handleRecurringAmountBlur}
                  error={!!recurringAmountError}
                  helperText={recurringAmountError}
                  placeholder="0.00"
                  startAdornment={<AttachMoney />}
                  test-id="hre-recurring-amount"
                />
              </Box>
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <TextField
            label="HRE Response"
            name="approvalResponse"
            value={approvalResponse}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              setApprovalResponse(e.target.value)
            }
            onBlur={() => {}}
            multiline
            rows={4}
            test-id="approval-response-input"
            className="w-100"
          />
        </Grid>
      </Grid>

      <Grid container spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Grid item xs={12} md={2} className="mt-2">
          <MUIButton
            onClick={handleBack}
            variant="outlined"
            fullWidth
            sx={{
              backgroundColor: 'white',
              borderColor: '#e0e0e0',
              color: '#000',
              '&:hover': {
                backgroundColor: '#f5f5f5',
                borderColor: '#bdbdbd',
              },
            }}
          >
            <ArrowBackIosIcon sx={{ fontSize: '1rem', mr: 0.5 }} /> Back
          </MUIButton>
        </Grid>
        <Grid item xs={12} md={10} className="mt-2">
          {showSaveButton && (
            <Button onClick={handleSelectCustomProgram} fullWidth test-id="hre-save-button">
              Save
            </Button>
          )}
          {showAddPaymentDetailsButton && (
            <Button onClick={handleSelectCustomProgram} fullWidth test-id="payment-program-custom-add-payment">
              Add Payment Details
            </Button>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
